import { Badge } from '@/components/ui/badge'
import { buttonVariants } from '@/components/ui/button'
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card'
import { cn } from '@/lib/tailwind.utils'
import { sidebarDispatchAtom } from '@app/state/sidebarAtom'
import { useSetAtom } from 'jotai'
import { ArrowRight } from 'lucide-react'
import type { RecentlyReleasedItem } from '../types'

const badgeStyles: Record<RecentlyReleasedItem['type'], string> = {
  'NEW FEATURE': 'bg-rexTeal-200 text-rexBlue-900',
  'TECH UPDATE': 'bg-orange-200 text-rexBlue-900',
  'BETA RELEASE': 'bg-indigo-100 text-rexBlue-900',
}

const actionLabel: Record<RecentlyReleasedItem['action'], string> = {
  tutorial: 'View tutorial',
  'release-notes': 'View release notes',
  'learn-more': 'Learn more',
  feedback: 'Give feedback',
  none: '',
}

export const RecentlyReleasedCard = ({ item }: { item: RecentlyReleasedItem }) => {
  const { type, title, description, action, href } = item
  const dispatchSidebarAction = useSetAtom(sidebarDispatchAtom)
  const isReleaseNotes = action === 'release-notes'
  const hasAction = action !== 'none'

  const card = (
    <Card className="flex h-full flex-col transition-colors hover:bg-neutral-50">
      <CardHeader className="pb-2">
        <Badge
          size={'xs'}
          className={cn('w-fit text-xs font-semibold uppercase', badgeStyles[type])}
        >
          {type}
        </Badge>
        <p className="text-sm font-semibold text-foreground">{title}</p>
      </CardHeader>

      <CardContent className="flex-1 pb-2">
        <p className="text-xs leading-relaxed text-muted-foreground">{description}</p>
      </CardContent>

      <CardFooter className="mt-auto justify-end pt-0">
        {hasAction && (
          <span
            className={cn(
              buttonVariants({ variant: 'ghost', size: 'sm' }),
              'pointer-events-none shrink-0 text-primary',
            )}
          >
            {actionLabel[action]}
            <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" />
          </span>
        )}
      </CardFooter>
    </Card>
  )

  return hasAction ? (
    isReleaseNotes ? (
      <button
        type="button"
        onClick={() => dispatchSidebarAction('toggleReleaseDialog')}
        aria-label={title}
        className="group block h-full w-full text-left"
      >
        {card}
      </button>
    ) : (
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        aria-label={`${title} (opens in a new tab)`}
        className="group block h-full"
      >
        {card}
      </a>
    )
  ) : (
    card
  )
}
